import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { preAnaestheticApi } from '../api/preAnaestheticApi';
import { mapBackendToForm, mapFormToBackend, validatePreAnaestheticForm } from '../utils/mapping';
import { useNotification } from '../../../../contexts/NotificationContext';
import { signatureMeta } from '../constants/options';
import { clearDraft, DRAFT_KEYS, loadDraft, saveDraft } from '../../../../utils/draftUtils';

export const usePreAnaestheticForm = (uhid, navigate) => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid || "",
    name: "",
    age: "",
    sex: "",
    op_no: "",
    pre_op_diagnosis: "",
    proposed_procedure: "",
    proposed_date_time: "",
    // CVS
    cvs_chest_pain: false,
    cvs_breathlessness: false,
    cvs_palpitation: false,
    cvs_hypertension: false,
    cvs_ihd: false,
    cvs_others: "",
    // RS
    rs_fever: false,
    rs_cough_sputum: false,
    rs_asthma: false,
    rs_copd: false,
    rs_pul_tb: false,
    rs_sneezing: false,
    rs_others: "",
    // GIT
    git_apd: false,
    git_jaundice: false,
    git_others: "",
    // GNS
    gns_stroke: false,
    gns_seizures: false,
    gns_polio: false,
    gns_loc: false,
    gns_head_injury: false,
    spine: "",
    // Endocrine
    endocrine_dm: false,
    endocrine_thyroid: "",
    endocrine_others: "",
    // GU
    gu_system_burns: false,
    gu_system_others: "",
    // Histories
    past_or_current_medications: "",
    previous_hospitalization: false,
    previous_surgery_anesthesia: false,
    drug_allergy: false,
    smoking: false,
    substance_abuse: false,
    alcoholism: false,
    pregnancy_lactation: false,
    breath_holding_time: "",
    effort_tolerance: "",
    personal_history_others: "",
    // Airway
    mpc: "",
    mo: "",
    neck_movement: "",
    mento_hyoid_distance: "",
    dentition: "",
    airway_assessment_others: "",
    // Vitals
    pr: "", bp: "", spo2: "", temp: "", ht: "", wt: "",
    riskfactors_concerns: "",
    // Exems
    conscious: false, oriented: false, pallor: false, febrile: false,
    icterus: false, cyanosis: false, clubbing: false, oedema: false,
    cvs: "", rs: "", git: "", gns: "",
    spine_evaluation: "", others_evaluation: "", experts_opinion: "",
    // Investigations
    hb: "", pcv: "", tc: "", dc: "", esr: "", platelets: "", bt: "", ct: "", pt: "", inr: "", aptt: "",
    blood_sugar: "", random_bs: "", fbs: "", ppbs: "", urea: "", creatinine: "", electrolytes: "",
    na: "", k: "", cl: "", hco3: "", blood_group_rh: "", cxr: "", ecg: "", echo: '', lft: '', tft: '', hiv: '', hbsag: "", vdrl: "",
    other_investigations: '',
    // Plan
    asa_grade: "", plan_of_anesthesia: "", pre_op_instructions: "", day_of_surgery_medications: "",
    instructions: "", informed_consent: "", npo_orders: "", blood_products: "", investigations: "", re_assessment: "", expert_opinion: "",
    // Signs
    anaesthesiologist_signature: "", anaesthesiologist_name: "", anaesthesiologist_date: "", anaesthesiologist_time: "",
    surgeon_signature: "", surgeon_name: "", surgeon_date: "", surgeon_time: "",
    nurse_signature: "", nurse_name: "", nurse_date: "", nurse_time: ""
  });

  const [errors, setErrors] = useState({});
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const [nextClicked, setNextClicked] = useState(false);

  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.PRE_ANAESTHETIC_FORM, uhid);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid]);

  const { data: fetchedData, isLoading: isInitialLoading } = useQuery({
    queryKey: ['preAnaesthetic', uhid],
    queryFn: async () => {
      try {
        const patientData = await preAnaestheticApi.getPatient(uhid);
        const ipData = await preAnaestheticApi.getNextIp();

        let evalData = {};
        try {
          evalData = await preAnaestheticApi.getEval(uhid);
        } catch (e) {
          console.log("No existing preAnaesthetic eval found.");
        }

        return { patientData, ipData, evalData };
      } catch (error) {
        showNotification('error', 'Failed to fetch form information');
        throw error;
      }
    },
    enabled: !!uhid,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!fetchedData) return;

    const { patientData, ipData, evalData } = fetchedData;

    if (evalData && Object.keys(evalData).length > 0) {
      setIsUpdate(true);
    } else {
      setIsUpdate(false);
    }

    const mappedData = mapBackendToForm(evalData);
    
    setFormData(prev => ({
      ...prev,
      ...mappedData,
      uhid_no: uhid,
      name: patientData?.name || mappedData.name || '',
      age: patientData?.age || mappedData.age || '',
      sex: patientData?.sex || mappedData.sex || '',
      ip_no: mappedData.ip_no || ipData?.next_ip_no || prev.ip_no
    }));
  }, [fetchedData, uhid]);

  const submitMutation = useMutation({
    mutationFn: (data) => {
      const payload = mapFormToBackend(data);
      if (isUpdate) {
        return preAnaestheticApi.updateEval(uhid, payload);
      }
      return preAnaestheticApi.createEval(payload);
    },
    onSuccess: () => {
      showNotification('success', isUpdate ? 'Pre-Anaesthetic Evaluation updated successfully!' : 'Pre-Anaesthetic Evaluation submitted successfully!');
      clearDraft(DRAFT_KEYS.PRE_ANAESTHETIC_FORM, uhid);
      navigate(`/mainPage/${uhid}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (err) => {
      const message = err.response?.data?.error || 'Failed to submit form';
      showNotification('error', message);
    }
  });

  const handleChange = useCallback((name) => (e, newValue) => {
    let value;
    if (e && e.target) {
      value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    } else {
      value = newValue;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [errors]);

  const handleNameChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  }, [errors]);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const validationErrors = validatePreAnaestheticForm({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: validationErrors[name] }));
    }
  };

  const handleSignatureOpen = (role) => {
    const meta = signatureMeta[role];
    if (meta) {
      const now = new Date();
      setFormData(prev => ({
        ...prev,
        [meta.dateKey]: now.toISOString().slice(0, 10),
        [meta.timeKey]: now.toTimeString().slice(0, 5),
      }));
    }
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const base64 = signaturePadRef.current.toDataURL().split(',')[1];
      setFormData(prev => ({ ...prev, [currentSignatureRole]: base64 }));
      setSignatureDialogOpen(false);
    }
  };

  const handleShare = async () => {
    try {
      const { token } = await preAnaestheticApi.generateShareToken(uhid);
      const link = `${window.location.origin}/preAnaestheticForm/${uhid}?token=${token}`;
      await navigator.clipboard.writeText(link);
      showNotification('success', 'Secure link copied to clipboard!');
      window.open(`https://wa.me/?text=${encodeURIComponent(`Dear Doctor, Please fill the evaluation: ${link}`)}`, '_blank');
    } catch (e) {
      showNotification('error', 'Failed to generate share link');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validatePreAnaestheticForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showNotification('error', 'Please fill required fields');
      const firstKey = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      return;
    }
    submitMutation.mutate(formData);
  };

  const handleNextClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.proposed_date_time) {
      showNotification('error', 'Please select a proposed date and time before continuing.');
    } else {
      setNextClicked(true);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    loading: isInitialLoading || submitMutation.isLoading,
    isInitialLoading,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    isUpdate,
    nextClicked,
    setNextClicked,
    handleChange,
    handleNameChange,
    handleBlur,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare,
    handleNextClick,
  };
};
